import { zodResolver } from '@hookform/resolvers/zod';
import {
  getApiErrorMessage,
  getIntegrationHttpGetAllSensorsQueryKey,
  useIntegrationHttpCreateSensor,
  useIntegrationHttpDeleteSensor,
  useIntegrationHttpGetAllSensors,
  useIntegrationHttpPreviewResponse,
  useIntegrationHttpUpdateSensor,
  type HttpHTTPSensor,
  type HttpPreviewResponseBody,
  type HttpSensorRequest,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  Braces,
  ChevronDown,
  Clock3,
  FlaskConical,
  Link2,
  Pencil,
  Plus,
  Server,
  Shield,
  SlidersHorizontal,
  Trash2,
  Wifi,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  Button,
  Modal,
  SegmentedControl,
  Select,
  SlideOutPanel,
  TextareaField,
  TextField,
  Toggle,
  cn,
} from '@/components/ui';

const headerSchema = z.object({
  name: z.string().min(1, 'Header name is required').max(255),
  value: z.string().max(2000),
});

const sensorFormSchema = z.object({
  humanId: z
    .string()
    .min(1, 'Human ID is required')
    .max(255)
    .regex(/^[a-z0-9.-]+$/, 'Use lowercase letters, numbers, dots, and hyphens only'),
  name: z.string().min(1, 'Name is required').max(255),
  enabled: z.boolean(),
  mode: z.enum(['poll', 'socket']),
  protocol: z.enum(['http', 'https']),
  method: z.enum(['GET', 'POST']),
  url: z.string().min(1, 'URL is required').max(1000),
  headers: z.array(headerSchema),
  basicAuthUsername: z.string().max(255).optional(),
  basicAuthPassword: z.string().max(255).optional(),
  successResponseCodes: z
    .string()
    .min(1, 'Success response codes are required')
    .refine(
      (value) =>
        parseSuccessCodes(value).length > 0 &&
        parseSuccessCodes(value).every(
          (code) => Number.isInteger(code) && code >= 100 && code <= 599
        ),
      'Enter comma-separated HTTP status codes'
    ),
  responseFormat: z.enum(['json']),
  jsonPath: z.string().min(1, 'JSON path is required').max(1000),
  transform: z.string().max(1000).optional(),
  timeoutSeconds: z.coerce.number().min(1, 'Minimum 1 second').max(300, 'Maximum 300 seconds'),
  pollIntervalSeconds: z.coerce
    .number()
    .min(1, 'Minimum 1 second')
    .max(86400, 'Maximum 86400 seconds'),
  sampleResponse: z.string().optional(),
});

type SensorFormData = z.infer<typeof sensorFormSchema>;
type CollapsibleSectionKey = 'authentication' | 'headers' | 'preview';

const defaultSampleResponse = `{
  "metrics": {
    "temperature": 21.5
  }
}`;

const emptyFormValues: SensorFormData = {
  humanId: '',
  name: '',
  enabled: true,
  mode: 'poll',
  protocol: 'https',
  method: 'GET',
  url: '',
  headers: [],
  basicAuthUsername: '',
  basicAuthPassword: '',
  successResponseCodes: '200',
  responseFormat: 'json',
  jsonPath: '$.metrics.temperature',
  transform: '',
  timeoutSeconds: 10,
  pollIntervalSeconds: 60,
  sampleResponse: defaultSampleResponse,
};

const defaultExpandedSections: Record<CollapsibleSectionKey, boolean> = {
  authentication: false,
  headers: false,
  preview: false,
};

export function HttpSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useIntegrationHttpGetAllSensors();

  const [creating, setCreating] = useState(false);
  const [editingSensor, setEditingSensor] = useState<HttpHTTPSensor | null>(null);
  const [deletingSensor, setDeletingSensor] = useState<HttpHTTPSensor | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useIntegrationHttpCreateSensor();
  const updateMutation = useIntegrationHttpUpdateSensor();
  const deleteMutation = useIntegrationHttpDeleteSensor();

  const sensors = data?.sensors ?? [];

  const invalidateSensors = () => {
    queryClient.invalidateQueries({ queryKey: getIntegrationHttpGetAllSensorsQueryKey() });
  };

  const handleCreate = async (payload: HttpSensorRequest) => {
    setFormError(null);
    try {
      await createMutation.mutateAsync({ data: payload });
      setCreating(false);
      invalidateSensors();
    } catch (error) {
      setFormError(getApiErrorMessage(error, 'Unable to create the HTTP sensor.'));
    }
  };

  const handleUpdate = async (payload: HttpSensorRequest) => {
    if (!editingSensor?.id) return;

    setFormError(null);
    try {
      await updateMutation.mutateAsync({ id: editingSensor.id, data: payload });
      setEditingSensor(null);
      invalidateSensors();
    } catch (error) {
      setFormError(getApiErrorMessage(error, 'Unable to update the HTTP sensor.'));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              HTTP Integration
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Poll HTTP endpoints and map their responses to sensors
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Add Sensor
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch HTTP sensors</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your HTTP sensor configuration.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {sensors.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No HTTP sensors configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add a sensor to start polling an HTTP endpoint.
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setCreating(true)}
              >
                Add Sensor
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Configured Sensors
              </h2>
              <div className="space-y-4">
                {sensors.map((sensor) => (
                  <SensorCard
                    key={sensor.id}
                    sensor={sensor}
                    onEdit={() => setEditingSensor(sensor)}
                    onDelete={() => setDeletingSensor(sensor)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <HttpSensorFormPanel
        isOpen={creating || !!editingSensor}
        sensor={editingSensor}
        error={formError}
        isLoading={createMutation.isPending || updateMutation.isPending}
        onClose={() => {
          setCreating(false);
          setEditingSensor(null);
          setFormError(null);
        }}
        onSubmit={editingSensor ? handleUpdate : handleCreate}
      />

      <Modal
        isOpen={!!deletingSensor}
        onClose={() => setDeletingSensor(null)}
        title="Delete HTTP sensor"
        subtitle={
          deletingSensor
            ? `Delete ${deletingSensor.name ?? deletingSensor.humanId}? This stops polling immediately.`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeletingSensor(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              leftIcon={<Trash2 className="h-4 w-4" />}
              onClick={async () => {
                if (!deletingSensor?.id) return;
                await deleteMutation.mutateAsync({ id: deletingSensor.id });
                setDeletingSensor(null);
                invalidateSensors();
              }}
              disabled={deleteMutation.isPending}
            >
              Delete Sensor
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          The sensor will be removed from the integration and unregistered from the shared sensor
          list.
        </p>
      </Modal>
    </div>
  );
}

function SensorCard({
  sensor,
  onEdit,
  onDelete,
}: {
  sensor: HttpHTTPSensor;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {sensor.name ?? sensor.humanId}
            </h3>
            <StatusBadge active={sensor.enabled} />
            <span className="rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-white/[0.08] dark:text-zinc-400">
              {sensor.method} {sensor.protocol}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{sensor.humanId}</p>
          <p className="mt-2 break-all font-mono text-xs text-gray-600 dark:text-zinc-400">
            {sensor.protocol}://{sensor.url}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Pencil className="h-4 w-4" />}
            onClick={onEdit}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 className="h-4 w-4" />}
            onClick={onDelete}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <InfoTile
          label="Polling"
          value={`${sensor.pollIntervalSeconds ?? 0}s interval`}
          detail={`Timeout ${sensor.timeoutSeconds ?? 0}s`}
          icon={<Wifi className="h-4 w-4" />}
        />
        <InfoTile
          label="Parsing"
          value={sensor.jsonPath ?? '—'}
          detail={sensor.transform ? `Transform: ${sensor.transform}` : 'No transform'}
          icon={<Server className="h-4 w-4" />}
        />
        <InfoTile
          label="Auth"
          value={sensor.basicAuthUsername ? sensor.basicAuthUsername : 'No basic auth'}
          detail={sensor.hasBasicAuthPassword ? 'Password configured' : 'No password'}
          icon={<Shield className="h-4 w-4" />}
        />
        <InfoTile
          label="Latest"
          value={
            sensor.currentValue !== undefined && sensor.currentValue !== null
              ? `${sensor.currentValue}`
              : 'No reading yet'
          }
          detail={
            sensor.lastStatusCode
              ? `Last status ${sensor.lastStatusCode}`
              : sensor.lastError
                ? sensor.lastError
                : 'No poll completed yet'
          }
          tone={sensor.lastError ? 'error' : 'default'}
        />
      </div>
    </div>
  );
}

function HttpSensorFormPanel({
  isOpen,
  sensor,
  error,
  isLoading,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  sensor: HttpHTTPSensor | null;
  error: string | null;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (payload: HttpSensorRequest) => Promise<void>;
}) {
  const previewMutation = useIntegrationHttpPreviewResponse();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [preview, setPreview] = useState<HttpPreviewResponseBody['preview'] | null>(null);
  const [expandedSections, setExpandedSections] = useState(defaultExpandedSections);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<SensorFormData>({
    resolver: zodResolver(sensorFormSchema),
    defaultValues: sensor ? toFormValues(sensor) : emptyFormValues,
  });

  useEffect(() => {
    if (!isOpen) return;

    reset(sensor ? toFormValues(sensor) : emptyFormValues);
    setPreview(null);
    setExpandedSections(defaultExpandedSections);
  }, [isOpen, sensor, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'headers',
  });

  const responseFormat = useWatch({ control, name: 'responseFormat' });
  const jsonPath = useWatch({ control, name: 'jsonPath' });
  const transform = useWatch({ control, name: 'transform' });
  const sampleResponse = useWatch({ control, name: 'sampleResponse' });
  const headers = useWatch({ control, name: 'headers' }) ?? [];
  const basicAuthUsername = useWatch({ control, name: 'basicAuthUsername' });
  const method = useWatch({ control, name: 'method' });
  const protocol = useWatch({ control, name: 'protocol' });
  const url = useWatch({ control, name: 'url' });
  const pollIntervalSeconds = useWatch({ control, name: 'pollIntervalSeconds' });
  const timeoutSeconds = useWatch({ control, name: 'timeoutSeconds' });

  const configuredHeaderCount = headers.filter((header) => header.name.trim()).length;
  const authenticationSummary = basicAuthUsername?.trim()
    ? `Basic auth as ${basicAuthUsername.trim()}`
    : sensor?.hasBasicAuthPassword
      ? 'Stored basic auth credentials'
      : 'No authentication';
  const headersSummary =
    configuredHeaderCount === 0
      ? 'No custom headers'
      : `${configuredHeaderCount} header${configuredHeaderCount === 1 ? '' : 's'}`;
  const previewSummary = sampleResponse?.trim() ? 'Sample response ready' : 'No sample loaded';

  const toggleSection = (section: CollapsibleSectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  useEffect(() => {
    if (!isOpen || !sampleResponse?.trim()) {
      setPreview(null);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      previewMutation.mutate(
        {
          data: {
            responseBody: sampleResponse,
            responseFormat,
            jsonPath,
            transform: transform?.trim() ? transform : undefined,
          },
        },
        {
          onSuccess: (result) => {
            setPreview(result.preview ?? null);
          },
          onError: () => {
            setPreview(null);
          },
        }
      );
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isOpen, sampleResponse, responseFormat, jsonPath, transform, previewMutation]);

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={sensor ? 'Edit HTTP Sensor' : 'Add HTTP Sensor'}
      subtitle="Configure how the endpoint is called, parsed, and reported."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(async (values) => onSubmit(toRequestData(values, sensor)))}
            disabled={isLoading}
          >
            {sensor ? 'Save Changes' : 'Create Sensor'}
          </Button>
        </>
      }
    >
      <form className="space-y-4">
        <SectionCard
          title="Overview"
          description="Name the sensor and choose how the integration should run."
          icon={<Server className="h-4 w-4" />}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Sensor Name"
              placeholder="Outdoor Temperature"
              error={errors.name?.message}
              {...register('name')}
            />
            <TextField
              label="Sensor Human ID"
              placeholder="outdoor-temperature"
              error={errors.humanId?.message}
              {...register('humanId')}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_auto]">
            <Controller
              name="mode"
              control={control}
              render={({ field }) => (
                <SegmentedControl
                  label="Mode"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'poll', label: 'Poll' },
                    { value: 'socket', label: 'Socket', disabled: true },
                  ]}
                  hint="Socket support is reserved for a future update."
                  error={errors.mode?.message}
                  fullWidth
                />
              )}
            />

            <div className="rounded-xl border border-gray-200 bg-white/80 px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
              <Controller
                name="enabled"
                control={control}
                render={({ field }) => (
                  <Toggle
                    label="Enabled"
                    description="Poll this sensor immediately after saving."
                    checked={field.value}
                    onChange={field.onChange}
                    layout="switch-right"
                  />
                )}
              />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Request"
          description="Set the endpoint and define which responses count as healthy."
          icon={<Link2 className="h-4 w-4" />}
          summary={
            url?.trim()
              ? summarizeEndpoint(method ?? 'GET', protocol ?? 'https', url)
              : 'Endpoint not configured'
          }
        >
          <div className="grid gap-4 md:grid-cols-[140px_140px_1fr]">
            <Controller
              name="protocol"
              control={control}
              render={({ field }) => (
                <Select
                  label="Protocol"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.protocol?.message}
                  options={[
                    { value: 'https', label: 'HTTPS' },
                    { value: 'http', label: 'HTTP' },
                  ]}
                />
              )}
            />
            <Controller
              name="method"
              control={control}
              render={({ field }) => (
                <Select
                  label="Method"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.method?.message}
                  options={[
                    { value: 'GET', label: 'GET' },
                    { value: 'POST', label: 'POST' },
                  ]}
                />
              )}
            />
            <TextField
              label="URL"
              placeholder="example.local/api/temperature"
              hint="Enter host/path here. Absolute URLs are also accepted."
              error={errors.url?.message}
              {...register('url')}
            />
          </div>

          <TextField
            className="md:max-w-sm"
            label="Success Response Codes"
            placeholder="200, 204"
            hint="Comma-separated HTTP status codes treated as successful reads."
            error={errors.successResponseCodes?.message}
            {...register('successResponseCodes')}
          />
        </SectionCard>

        <SectionCard
          title="Parsing"
          description="Extract the sensor value from the response and optionally transform it."
          icon={<Braces className="h-4 w-4" />}
          summary={transform?.trim() ? 'JSON path with transform' : 'JSON path only'}
        >
          <div className="grid gap-4 md:grid-cols-[180px_1fr]">
            <Controller
              name="responseFormat"
              control={control}
              render={({ field }) => (
                <Select
                  label="Response Format"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.responseFormat?.message}
                  options={[{ value: 'json', label: 'JSON' }]}
                />
              )}
            />
            <TextField
              label="JSON Path"
              placeholder="$.metrics.temperature"
              error={errors.jsonPath?.message}
              {...register('jsonPath')}
            />
          </div>

          <TextField
            label="Transform"
            placeholder="value * 100"
            hint="Optional expression applied after extraction."
            error={errors.transform?.message}
            {...register('transform')}
          />
        </SectionCard>

        <SectionCard
          title="Scheduling"
          description="Choose how often to poll and how long to wait for each request."
          icon={<Clock3 className="h-4 w-4" />}
          summary={`Every ${pollIntervalSeconds ?? 0}s, timeout ${timeoutSeconds ?? 0}s`}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Poll Interval (seconds)"
              type="number"
              min={1}
              max={86400}
              error={errors.pollIntervalSeconds?.message}
              {...register('pollIntervalSeconds')}
            />
            <TextField
              label="Timeout (seconds)"
              type="number"
              min={1}
              max={300}
              error={errors.timeoutSeconds?.message}
              {...register('timeoutSeconds')}
            />
          </div>
        </SectionCard>

        <CollapsibleSectionCard
          title="Authentication"
          description="Only needed for endpoints protected by HTTP basic auth."
          icon={<Shield className="h-4 w-4" />}
          summary={authenticationSummary}
          isExpanded={expandedSections.authentication}
          onToggle={() => toggleSection('authentication')}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label="Basic Auth Username"
              placeholder="demo"
              error={errors.basicAuthUsername?.message}
              {...register('basicAuthUsername')}
            />
            <TextField
              label="Basic Auth Password"
              placeholder={
                sensor?.hasBasicAuthPassword ? 'Leave empty to keep current password' : 'secret'
              }
              type="password"
              error={errors.basicAuthPassword?.message}
              {...register('basicAuthPassword')}
            />
          </div>
          {sensor?.hasBasicAuthPassword && (
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              Leaving the password blank keeps the existing secret unchanged.
            </p>
          )}
        </CollapsibleSectionCard>

        <CollapsibleSectionCard
          title="Headers"
          description="Add custom request headers for API keys, content negotiation, or vendor-specific requirements."
          icon={<SlidersHorizontal className="h-4 w-4" />}
          summary={headersSummary}
          isExpanded={expandedSections.headers}
          onToggle={() => toggleSection('headers')}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              Only add headers your endpoint explicitly requires.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => append({ name: '', value: '' })}
            >
              Add Header
            </Button>
          </div>

          <div className="space-y-3">
            {fields.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white/80 px-4 py-5 text-sm text-gray-500 dark:border-white/[0.08] dark:bg-[#18181b] dark:text-zinc-500">
                No custom headers configured.
              </div>
            ) : (
              fields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-xl border border-gray-200 bg-white/80 p-3 dark:border-white/[0.06] dark:bg-[#18181b]"
                >
                  <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                    <TextField
                      label={index === 0 ? 'Header Name' : ' '}
                      hideLabel={index !== 0}
                      placeholder="X-API-Key"
                      error={errors.headers?.[index]?.name?.message}
                      {...register(`headers.${index}.name`)}
                    />
                    <TextField
                      label={index === 0 ? 'Header Value' : ' '}
                      hideLabel={index !== 0}
                      placeholder="secret"
                      error={errors.headers?.[index]?.value?.message}
                      {...register(`headers.${index}.value`)}
                    />
                    <div className="pt-6">
                      <Button type="button" variant="ghost" size="sm" onClick={() => remove(index)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CollapsibleSectionCard>

        <CollapsibleSectionCard
          title="Preview"
          description="Use a sample payload to validate your JSON path and transform before saving."
          icon={<FlaskConical className="h-4 w-4" />}
          summary={previewSummary}
          isExpanded={expandedSections.preview}
          onToggle={() => toggleSection('preview')}
        >
          <TextareaField
            label="Sample Response"
            rows={8}
            error={errors.sampleResponse?.message}
            hint="Used only for the live parsing preview."
            {...register('sampleResponse')}
          />

          <PreviewPanel preview={preview} isLoading={previewMutation.isPending} />
        </CollapsibleSectionCard>

        {error && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-500">
            {error}
          </div>
        )}
      </form>
    </SlideOutPanel>
  );
}

function SectionCard({
  title,
  description,
  icon,
  summary,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  summary?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/80 shadow-sm dark:border-white/[0.06] dark:bg-zinc-900/70">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200/80 px-5 py-4 dark:border-white/[0.06]">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-xl border border-gray-200 bg-white p-2 text-gray-600 dark:border-white/[0.08] dark:bg-[#18181b] dark:text-zinc-300">
            {icon}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{description}</p>
          </div>
        </div>
        {summary && (
          <div className="max-w-full rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 dark:border-white/[0.08] dark:bg-[#18181b] dark:text-zinc-400">
            <span className="block truncate">{summary}</span>
          </div>
        )}
      </div>

      <div className="space-y-4 px-5 py-5">{children}</div>
    </section>
  );
}

function CollapsibleSectionCard({
  title,
  description,
  icon,
  summary,
  isExpanded,
  onToggle,
  children,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  summary: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50/80 shadow-sm dark:border-white/[0.06] dark:bg-zinc-900/70">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-gray-100/70 dark:hover:bg-white/[0.03]"
      >
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 rounded-xl border border-gray-200 bg-white p-2 text-gray-600 dark:border-white/[0.08] dark:bg-[#18181b] dark:text-zinc-300">
            {icon}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
              <span className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:border-white/[0.08] dark:bg-[#18181b] dark:text-zinc-500">
                Optional
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">{description}</p>
            <p className="mt-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-zinc-500">
              {summary}
            </p>
          </div>
        </div>

        <ChevronDown
          className={cn(
            'mt-1 h-5 w-5 flex-shrink-0 text-gray-400 transition-transform dark:text-zinc-600',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {isExpanded && (
        <div className="space-y-4 border-t border-gray-200/80 px-5 py-5 dark:border-white/[0.06]">
          {children}
        </div>
      )}
    </section>
  );
}

function PreviewPanel({
  preview,
  isLoading,
}: {
  preview: HttpPreviewResponseBody['preview'] | null;
  isLoading: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-zinc-900">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">Live Preview</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
            Checks your JSON path and transform against the sample response.
          </p>
        </div>
        {isLoading && (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        )}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <PreviewValue
          label="Extracted Value"
          value={
            preview?.extractedValue !== undefined && preview?.extractedValue !== null
              ? `${preview.extractedValue}`
              : (preview?.extractError ?? 'No preview yet')
          }
          tone={preview?.extractError ? 'error' : 'default'}
        />
        <PreviewValue
          label="Transformed Value"
          value={
            preview?.transformedValue !== undefined && preview?.transformedValue !== null
              ? `${preview.transformedValue}`
              : (preview?.transformError ?? 'No transform applied')
          }
          tone={preview?.transformError ? 'error' : 'default'}
        />
      </div>
    </div>
  );
}

function PreviewValue({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'error';
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
        {label}
      </div>
      <div
        className={`mt-2 break-all font-mono text-sm ${
          tone === 'error' ? 'text-red-500' : 'text-gray-900 dark:text-white'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function InfoTile({
  label,
  value,
  detail,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  detail: string;
  icon?: React.ReactNode;
  tone?: 'default' | 'error';
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/[0.06] dark:bg-zinc-900">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{value}</div>
      <div
        className={`mt-1 text-xs ${tone === 'error' ? 'text-red-500' : 'text-gray-500 dark:text-zinc-500'}`}
      >
        {detail}
      </div>
    </div>
  );
}

function StatusBadge({ active }: { active?: boolean }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
        active
          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
          : 'bg-gray-200 text-gray-700 dark:bg-zinc-800 dark:text-zinc-400'
      }`}
    >
      {active ? 'Enabled' : 'Disabled'}
    </span>
  );
}

function toFormValues(sensor: HttpHTTPSensor): SensorFormData {
  return {
    humanId: sensor.humanId ?? '',
    name: sensor.name ?? '',
    enabled: sensor.enabled ?? true,
    mode: sensor.mode === 'socket' ? 'socket' : 'poll',
    protocol: sensor.protocol === 'http' ? 'http' : 'https',
    method: sensor.method === 'POST' ? 'POST' : 'GET',
    url: sensor.url ?? '',
    headers:
      sensor.headers?.map((header) => ({
        name: header.name ?? '',
        value: header.value ?? '',
      })) ?? [],
    basicAuthUsername: sensor.basicAuthUsername ?? '',
    basicAuthPassword: '',
    successResponseCodes: (sensor.successResponseCodes ?? [200]).join(', '),
    responseFormat: sensor.responseFormat === 'json' ? 'json' : 'json',
    jsonPath: sensor.jsonPath ?? '$.metrics.temperature',
    transform: sensor.transform ?? '',
    timeoutSeconds: sensor.timeoutSeconds ?? 10,
    pollIntervalSeconds: sensor.pollIntervalSeconds ?? 60,
    sampleResponse: defaultSampleResponse,
  };
}

function toRequestData(
  values: SensorFormData,
  existingSensor: HttpHTTPSensor | null
): HttpSensorRequest {
  const username = values.basicAuthUsername?.trim() ?? '';
  const password = values.basicAuthPassword?.trim() ?? '';

  const payload: HttpSensorRequest = {
    humanId: values.humanId.trim(),
    name: values.name.trim(),
    enabled: values.enabled,
    mode: 'poll',
    protocol: values.protocol,
    method: values.method,
    url: values.url.trim(),
    headers: values.headers
      .filter((header) => header.name.trim())
      .map((header) => ({ name: header.name.trim(), value: header.value })),
    successResponseCodes: parseSuccessCodes(values.successResponseCodes),
    responseFormat: values.responseFormat,
    jsonPath: values.jsonPath.trim(),
    timeoutSeconds: Number(values.timeoutSeconds),
    pollIntervalSeconds: Number(values.pollIntervalSeconds),
  };

  if (username) {
    payload.basicAuthUsername = username;
    if (password || !existingSensor?.hasBasicAuthPassword) {
      payload.basicAuthPassword = password;
    }
  } else {
    payload.basicAuthUsername = '';
    payload.basicAuthPassword = '';
  }

  if (values.transform?.trim()) {
    payload.transform = values.transform.trim();
  }

  return payload;
}

function parseSuccessCodes(value: string): number[] {
  return value
    .split(',')
    .map((item) => Number.parseInt(item.trim(), 10))
    .filter((item) => Number.isFinite(item));
}

function summarizeEndpoint(method: string, protocol: string, url: string): string {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return 'Endpoint not configured';

  const displayUrl = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `${protocol}://${trimmedUrl}`;
  return `${method} ${displayUrl}`;
}
